import {cloneTemplate} from '../../Library/WebComponents';

/**
 * @type {Map<HTMLElement, CallableFunction>}
 */
const clickListeners = new Map();

/**
 * @type {Map<HTMLElement, CallableFunction>}
 */
const tabListeners = new Map();

export class FocusInput extends CustomEvent {
    /**
     * @param {HTMLElement} tagsContainer
     */
    constructor(tagsContainer) {
        super('focusinput');

        this.tagsContainer = tagsContainer;
    }
}

export class BlurInput extends CustomEvent {
    /**
     * @param {HTMLElement} tagsContainer
     */
    constructor(tagsContainer) {
        super('blurinput');

        this.tagsContainer = tagsContainer;
    }
}

export class TagInputContent extends CustomEvent {
    /**
     * @param {HTMLElement} tagsContainer
     */
    constructor(tagsContainer) {
        super('taginputcontent');

        this.tagsContainer = tagsContainer;
    }
}

export class RemoveEditableTag extends CustomEvent {
    /**
     * @param {HTMLElement} tagsContainer
     * @param {HTMLElement} tagElement
     */
    constructor(tagsContainer, tagElement) {
        super('removeeditabletag');

        this.tagsContainer = tagsContainer;
        this.tagElement = tagElement;
    }
}

export class RemoveLastTag extends CustomEvent {
    /**
     * @param {HTMLElement} tagsContainer
     */
    constructor(tagsContainer) {
        super('removelasttag');

        this.tagsContainer = tagsContainer;
    }
}

/**
 * @param {HTMLElement} inputArea
 * @param {HTMLElement} tagsContainer
 * @returns {(function(MouseEvent))|*}
 */
const unfocusTagsContainerIfClickOutside = (inputArea, tagsContainer) => event => {
    if (!(event.target.closest('.input-area') === inputArea)) {
        document.dispatchEvent(new BlurInput(tagsContainer));
    }
};

/**
 * @param {HTMLElement} tagsContainer
 * @returns {(function(KeyboardEvent))|*}
 */
const unfocusTagsContainerIfTabbedAway = tagsContainer => event => {
    if (event.key === 'Tab') {
        document.dispatchEvent(new BlurInput(tagsContainer));
    }
};

/**
 * @param {FocusInput} event
 */
const focusInput = event => {
    const tagsContainer = event.tagsContainer;

    const textInput = tagsContainer.querySelector('input[type=text]');

    if (textInput.disabled) {
        return;
    }

    textInput.focus();

    const inputArea = tagsContainer.querySelector('.input-area');
    inputArea.classList.add('focus');

    if (!clickListeners.has(tagsContainer)) {
        clickListeners.set(tagsContainer, unfocusTagsContainerIfClickOutside(inputArea, tagsContainer));
        tabListeners.set(tagsContainer, unfocusTagsContainerIfTabbedAway(tagsContainer));

        document.addEventListener('click', clickListeners.get(tagsContainer));
        document.addEventListener('keydown', tabListeners.get(tagsContainer));
    }
};

/**
 * @param {FocusInput} event
 */
const blurInput = event => {
    document.dispatchEvent(new TagInputContent(event.tagsContainer));

    document.removeEventListener('click', clickListeners.get(event.tagsContainer));
    document.removeEventListener('keydown', tabListeners.get(event.tagsContainer));

    clickListeners.delete(event.tagsContainer);
    tabListeners.delete(event.tagsContainer);

    event.tagsContainer.querySelector('.input-area').classList.remove('focus');
};

const syncCapacity = container => {
    const maximum = Number(container.dataset.maxTags);

    if (!maximum) {
        return;
    }

    const textInput = container.querySelector('input[type=text]');
    const full      = container.querySelectorAll('.input-area .tag').length >= maximum;

    textInput.disabled    = full;
    textInput.placeholder = full ? container.dataset.limitPlaceholder : container.dataset.placeholder;
};

/**
 * @param {TagInputContent} event
 */
const tagInputContent = event => {
    const container = event.tagsContainer;
    const textInput = container.querySelector('input[type=text]');
    const value = textInput.value;

    if (value === '') {
        return;
    }

    const component = cloneTemplate('#editable-tags-span');

    component.querySelector('span').textContent = value;

    const newOption = document.createElement('option');

    newOption.value = value;
    newOption.selected = true;

    const select = container.querySelector('select');

    select.appendChild(newOption);
    select.dispatchEvent(new Event('input', {bubbles: true}));

    container.querySelector('.input-area').insertBefore(component, textInput);

    textInput.value = '';

    syncCapacity(container);
};

const removeTag = (container, tag) => {
    container.querySelector('.input-area').removeChild(tag);
    container.querySelector('input[type=text]').focus();

    const select = container.querySelector('select');

    const option = select.querySelector(`[value="${tag.querySelector('span').textContent}"]`);

    select.removeChild(option);
    select.dispatchEvent(new Event('input', {bubbles: true}));

    syncCapacity(container);
};

/**
 * @param {RemoveLastTag} event
 */
const removeLastTag = event => {
    const tags = event.tagsContainer.querySelectorAll('.input-area .tag');

    if (!tags.length) {
        return;
    }

    removeTag(event.tagsContainer, tags.item(tags.length - 1));
};

/**
 * @param {RemoveEditableTag} event
 */
const removeEditableTag = event => {
    removeTag(event.tagsContainer, event.tagElement);
};

document.addEventListener('focusinput', focusInput);
document.addEventListener('blurinput', blurInput);
document.addEventListener('taginputcontent', tagInputContent);
document.addEventListener('removeeditabletag', removeEditableTag);
document.addEventListener('removelasttag', removeLastTag);
